<template>
  <!-- The whole world, no pin, nothing pre-chosen. A guessed default was worse
       than none: IP geolocation put every birth in Redmond, because that is
       where the hop resolves, not where the seeker is. So the map asks instead
       of assuming, and the button below it carries the instruction. -->
  <div class="bp">
    <div class="bp-frame">
      <div ref="mapEl" class="bp-map" :class="{ 'is-waiting': !hasPin }"></div>
      <!-- The instruction belongs on the thing it instructs. It sat in a
           paragraph above the map, so the largest object on the page carried
           no invitation of its own. Fades on the first pin. -->
      <p v-if="!hasPin" class="bp-invite">Tap anywhere on Earth</p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import axios from 'axios'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { static_store } from '@/store'
import { tileSource } from '@/utils/map-tiles'

/* Leaflet's default marker is a blue teardrop loaded from its own sprite, which
   is both off-palette and a broken image under a bundler. A div is lighter and
   can just be gold. */
const PIN = L.divIcon({
  className: 'bp-pin',
  html: '<span class="bp-pin-dot"></span>',
  iconSize: [18, 18],
  iconAnchor: [9, 9],
})

const props = defineProps({
  // A point already chosen ({ lat, lng }): the pin is put back where it was,
  // without asking for its name again (the parent still holds it).
  initial: { type: Object, default: null },
})
const emit = defineEmits(['update'])

const mapEl = ref(null)
const pinLat = ref(null)
const pinLng = ref(null)

let map = null
let marker = null
let resizeWatcher = null

const hasPin = computed(() => pinLat.value !== null && pinLng.value !== null)

/** What to call the point. Asked of our own backend, which reads the geocoder
 *  for land and our table of seas for water -- the geocoder cannot name water
 *  at all, and most of the world is water. */
async function nameFor(la, ln) {
  emit('update', { name: '', lat: la, lng: ln, naming: true })
  try {
    const token = localStorage.getItem('authToken')
    const { data } = await axios.get(
      `${static_store.apiUrl}/reader/become-mystic/place-name/`,
      { params: { lat: la, lng: ln },
        headers: { Authorization: `Token ${token}` }, timeout: 12000 })
    emit('update', {
      name: data.place || '', lat: la, lng: ln,
      // The instant the click landed, stamped on a server clock. This is what
      // the chart is cast from, so it must not be read off the device.
      moment: data.moment || '', naming: false,
    })
  } catch {
    // Never strand the seeker on a failed lookup: the point is what the chart
    // needs, so let the coordinates stand in as the name.
    emit('update', {
      name: `${la.toFixed(2)}, ${ln.toFixed(2)}`, lat: la, lng: ln,
      moment: new Date().toISOString(), naming: false,
    })
  }
}

function placeMarker(la, ln) {
  pinLat.value = la
  pinLng.value = ln
  if (!marker) {
    marker = L.marker([la, ln], { draggable: true, icon: PIN }).addTo(map)
    marker.on('dragend', () => {
      const p = marker.getLatLng()
      dropPin(p.lat, p.lng)
    })
  } else {
    marker.setLatLng([la, ln])
  }
}

function dropPin(la, ln) {
  placeMarker(la, ln)
  nameFor(la, ln)
}

onMounted(async () => {
  await nextTick()
  map = L.map(mapEl.value, { worldCopyJump: true, minZoom: 1 }).setView([20, 0], 1)
  // Keyed provider in prod, OSM on a dev box -- see utils/map-tiles for why.
  const tiles = tileSource(static_store.maptiler_key)
  L.tileLayer(tiles.url, tiles.options).addTo(map)

  map.on('click', (e) => dropPin(e.latlng.lat, e.latlng.lng))
  const at = props.initial
  if (at && Number.isFinite(Number(at.lat)) && Number.isFinite(Number(at.lng))) placeMarker(Number(at.lat), Number(at.lng))

  // Leaflet caches its container size, so it renders a strip of bare
  // background whenever the box changes underneath it: the threshold fading
  // in, and the two-column grid collapsing to one at 900px. A timeout only
  // covers the first of those, so watch the element instead.
  setTimeout(() => { if (map) map.invalidateSize() }, 220)
  if (typeof ResizeObserver !== 'undefined') {
    resizeWatcher = new ResizeObserver(() => { if (map) map.invalidateSize() })
    resizeWatcher.observe(mapEl.value)
  }
})

onBeforeUnmount(() => {
  if (resizeWatcher) { resizeWatcher.disconnect(); resizeWatcher = null }
  if (map) { map.remove(); map = null }
})
</script>

<style scoped>
/* Leaflet's panes and controls (z-index 400 to 1000) stay inside the map, so
   the threshold's sticky Create button passes over them on a phone. */
.bp { width: 100%; margin: 0; isolation: isolate; }
.bp-frame { position: relative; }
.bp-invite {
  position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%);
  /* Above Leaflet's panes, which sit at 400 and up. Without this the label is
     in the DOM and painted underneath the tiles. */
  z-index: 900;
  margin: 0; padding: 9px 18px; pointer-events: none;
  font-family: 'Cinzel', serif; font-size: 13px; letter-spacing: 1.4px;
  text-transform: uppercase; color: #f0d9a3; white-space: nowrap;
  background: rgba(10, 4, 22, 0.62);
  border: 1px solid rgba(201, 169, 78, 0.36); border-radius: 999px;
  backdrop-filter: blur(2px);
  animation: bp-breathe 3.4s ease-in-out infinite;
}
@keyframes bp-breathe {
  0%, 100% { opacity: 0.78; }
  50%      { opacity: 1; }
}
@media (prefers-reduced-motion: reduce) {
  .bp-invite { animation: none; opacity: 0.92; }
}
.bp-map {
  height: 244px; border-radius: 12px; overflow: hidden;
  border: 1px solid rgba(201, 169, 78, 0.22);
  background: #120a22;
  cursor: crosshair;
}
/* Until a point is chosen the map is the question being asked, so it glows
   faintly. Once the pin lands the glow goes and the button takes over. */
.bp-map.is-waiting { border-color: rgba(201, 169, 78, 0.45); }

/* OSM's raster tiles are daylight-bright. Inverting and rotating the hue sits
   them in the same violet dark as everything else, and costs nothing over
   taking on a second tile provider's terms. Scoped to the TILE pane: filtering
   the whole container inverts the pin and the controls with it. */
.bp-map :deep(.leaflet-tile-pane) {
  filter: invert(0.94) hue-rotate(205deg) saturate(0.38) brightness(0.92) contrast(1.06);
}
.bp-map :deep(.bp-pin-dot) {
  display: block; width: 18px; height: 18px; border-radius: 50%;
  background: #f0d9a3;
  border: 2px solid #1a0f2e;
  box-shadow: 0 0 0 3px rgba(240, 217, 163, 0.32), 0 2px 8px rgba(0, 0, 0, 0.6);
}
.bp-map :deep(.leaflet-control-zoom a) {
  color: #f0d9a3; background: rgba(26, 15, 46, 0.92);
  border-color: rgba(201, 169, 78, 0.3);
}
.bp-map :deep(.leaflet-control-zoom a:hover) { background: rgba(40, 24, 68, 0.95); }
.bp-map :deep(.leaflet-control-attribution) {
  background: rgba(10, 4, 22, 0.7); color: rgba(195, 169, 255, 0.55);
  font-size: 9px;
}
.bp-map :deep(.leaflet-control-attribution a) { color: rgba(201, 169, 78, 0.75); }

@media (max-width: 800px) {
  /* Stacked it runs the full measure, so a 280px strip reads as a letterbox. */
  .bp-map { height: 300px; }
}
@media (max-width: 480px) {
  .bp-map { height: 260px; }
}
</style>
